'use client';

import React from 'react';
import { formatCurrency } from '@/lib/utils';

interface FareData {
  totalAmount: number;
  totalRefundableAmount: number;
  adminCancelCharges: number;
  airlinecancellationFee: number;
  nonRefundableAmount: number;
}

interface ReviewCancellationProps {
  fareData: {
    data: FareData[];
  };
}

const ReviewCancellation = ({ fareData }: ReviewCancellationProps) => {
  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8">
      {/* Header */}
      <h1 className="
        text-lg sm:text-xl lg:text-2xl 
        font-semibold 
        text-zinc-800
        px-1
      ">
        Please check refund amount for selected passengers
      </h1>

      {/* Total Paid Section */}
      <div className="
        mt-4 sm:mt-6 lg:mt-8 
        rounded-lg 
        border-2 border-light
        overflow-hidden
      ">
        {/* Total Amount Header */}
        <div className="
          flex justify-between items-center
          border-b-2 border-light 
          p-3 sm:p-4 
          bg-blue-50/50
        ">
          <span className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800">
            Total Paid
          </span>
          <span className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800">
            {formatCurrency(fareData?.data[0]?.totalAmount)}
          </span>
        </div>

        {/* Fare Breakdown */}
        <div className="
          space-y-2 
          p-3 sm:p-4 
          text-sm sm:text-base 
          text-gray-800
        ">
          <div className="flex justify-between items-center">
            <span>Airline Fare</span>
            <span>{formatCurrency(fareData?.data[0]?.airlineFare || 0)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Convenience Fee</span>
            <span>
              {formatCurrency(fareData?.data[0]?.nonRefundableAmount || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Deductions Section */}
      <div className="
        mt-4 sm:mt-6 lg:mt-8 
        rounded-lg 
        border-2 border-light
        overflow-hidden
      ">
        {/* Deductions Header */}
        <div className="
          flex justify-between items-center
          border-b-2 border-light 
          p-3 sm:p-4 
          bg-blue-50/50
        ">
          <span className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800">
            Deductions
          </span>
          <span className="text-base sm:text-lg lg:text-xl font-semibold text-red-600">
            -{formatCurrency(
              fareData?.data[0]?.totalAmount -
              fareData?.data[0]?.totalRefundableAmount || 0,
            )}
          </span>
        </div>

        {/* Cancellation Fees */}
        <div className="
          border-b-2 border-light 
          p-3 sm:p-4
        ">
          <div className="
            text-sm sm:text-base 
            font-semibold 
            text-gray-800 
            mb-2
          ">
            Cancellation & Service Fee
          </div>
          <div className="
            flex justify-between items-center
            text-sm sm:text-base 
            text-gray-800
          ">
            <span>Airline Cancellation Fee</span>
            <span className="text-red-600">
              -{formatCurrency(fareData?.data[0]?.airlinecancellationFee || 0)}
            </span>
          </div>
        </div>

        {/* Non-Refundable Components */}
        <div className="p-3 sm:p-4">
          <div className="
            text-sm sm:text-base 
            font-semibold 
            text-gray-800 
            mb-2
          ">
            Non-Refundable Fare Component
          </div>
          <div className="
            flex justify-between items-center
            text-sm sm:text-base 
            text-gray-800
          ">
            <span>Non-Refundable</span>
            <span>
              {formatCurrency(fareData?.data[0]?.nonRefundableAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Total Refund Amount - Optional Enhancement */}
      <div className="
        mt-6 sm:mt-8 
        p-3 sm:p-4
        rounded-lg 
        bg-green-50 
        border-2 border-green-200
      ">
        <div className="
          flex justify-between items-center
          text-base sm:text-lg lg:text-xl 
          font-semibold 
          text-green-700
        ">
          <span>Total Refund Amount</span>
          <span>
            {formatCurrency(fareData?.data[0]?.totalRefundableAmount)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReviewCancellation;
